import { createAppSlice } from "../../app/createAppSlice";
import { ng } from "@common/ngds";
import { designProject, type DesignReviewPayload } from "../projects/projectSlice";

// Staging area for a completed design run, kept OUTSIDE ProjectSlice on purpose.
//
// When a design finishes, the server returns the recommended project (the CodeCheckOutput the
// design engine produced) plus the full list of AutoDesignSuggestions. Rather than applying that
// result straight to the project (and auto-persisting it), we hold it here and open a review
// modal. The user then approves the recommended suggestion, picks a different one, or declines.
// Only that explicit choice mutates ProjectSlice — so nothing here can ever trip the sync
// middleware's auto-save (which only reacts to `ProjectSlice/` actions).
export interface DesignReviewState {
  // Whether the review modal should be shown.
  open: boolean;
  // The recommended project returned by the design engine (applied verbatim if the user approves
  // the recommended suggestion). Null until a design run completes.
  recommendedProject: ng.frontend.column.Project_json | null;
  // Every suggestion the design engine returned. Exactly one has isRecommended === true when the
  // list is non-empty. An empty list means the design constraints were too tight to satisfy.
  suggestions: ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json[];
}

const initialState: DesignReviewState = {
  open: false,
  recommendedProject: null,
  suggestions: [],
};

export const DesignReviewSlice = createAppSlice({
  name: "DesignReview",
  initialState,
  reducers: {
    // Dismiss the modal and clear the staged result. Used both when the user declines/cancels
    // and after a suggestion has been applied to the project.
    closeDesignReview(state) {
      state.open = false;
      state.recommendedProject = null;
      state.suggestions = [];
    },
  },
  extraReducers: (builder) => {
    // A fresh run is starting — drop any stale staged result and keep the modal closed until it
    // completes.
    builder.addCase(designProject.pending, (state) => {
      state.open = false;
      state.recommendedProject = null;
      state.suggestions = [];
    });
    // Stage the result and open the modal. We open even when `suggestions` is empty so the modal
    // can tell the user the constraints were too tight (and leave the project untouched).
    builder.addCase(designProject.fulfilled, (state, action) => {
      const payload = action.payload as DesignReviewPayload;
      state.recommendedProject = payload.recommendedProject ?? null;
      state.suggestions = payload.suggestions ?? [];
      state.open = true;
      // [design-suggestions debug] What actually got staged for the review modal. If this logs 0
      // but the server logs a positive count, the loss is in transport/parsing above.
      console.log(
        "[design-suggestions debug] designReviewSlice staged suggestions:",
        state.suggestions.length,
        "| recommendedProject present:",
        state.recommendedProject != null,
      );
    });
    builder.addCase(designProject.rejected, (state) => {
      state.open = false;
      state.recommendedProject = null;
      state.suggestions = [];
    });
  },
  selectors: {
    selectDesignReviewOpen: (state: DesignReviewState) => state.open,
    selectDesignReviewSuggestions: (state: DesignReviewState) => state.suggestions,
    selectDesignReviewRecommendedProject: (state: DesignReviewState) => state.recommendedProject,
  },
});

export const { closeDesignReview } = DesignReviewSlice.actions;
export const { selectDesignReviewOpen, selectDesignReviewSuggestions, selectDesignReviewRecommendedProject } =
  DesignReviewSlice.selectors;
